import { Component, inject, signal, computed, DestroyRef, effect } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { fromEvent, forkJoin, timer, filter } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DOCUMENT } from '@angular/common';
import { environment } from '../environments/environment';
import { RouterOutlet } from '@angular/router';
import { slideInAnimation, TabMenuComponent } from '@lp-ui-lib';
import { AuthenticationService } from '@lp-ui-lib';
import { StorageService } from '@lp-ui-lib';
import { UiService } from '@lp-ui-lib';
import { AssetsService } from '@lp-ui-lib';
import { SettingsService } from '@lp-ui-lib';
import { UtilService } from '@lp-ui-lib';
import { NotificationService, NotificationStore } from '@lp-ui-lib';
import { SwPush } from '@angular/service-worker';
import { I18nService } from '@lp-ui-lib';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { SplashScreenComponent } from '@lp-ui-lib';
import { DesktopHeaderComponent } from '@lp-ui-lib';
import { FooterComponent } from '@lp-ui-lib';
//import { BannerMaintenanceComponent } from '@lp-ui-lib';
import { BannerNotificationService } from '@lp-ui-lib';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    NzModalModule,
    TabMenuComponent,
    SplashScreenComponent,
    DesktopHeaderComponent,
    //BannerMaintenanceComponent,
    FooterComponent,
  ],
  templateUrl: './app.html',
  styleUrl: './app.scss',
  animations: [slideInAnimation],
})
export class App {
  private router = inject(Router);
  private auth = inject(AuthenticationService);
  private storage = inject(StorageService);
  private ui = inject(UiService);
  private assets = inject(AssetsService);
  private settings = inject(SettingsService);
  private util = inject(UtilService);
  private notifications = inject(NotificationService);
  private notificationStore = inject(NotificationStore);
  private swPush = inject(SwPush);
  public i18n = inject(I18nService);
  private document = inject(DOCUMENT);
  private destroyRef = inject(DestroyRef);
  public bannerMaintenance = inject(BannerNotificationService);

  Internet = signal('No internet connection');
  connectionStatus = signal<'online' | 'offline'>('online');
  modalOpen = signal(false);
  showBannerMaintenance = signal(false);
  loading = signal(false);
  loadingError = signal(false);
  isOtFetched = signal(false);

  readonly REFRESH_INTERVAL = 1000 * 60 * 1;

  isAuth = computed(() => this.auth.isLoggedIn());
  isDesktop = computed(() => this.ui.IsDesktop());
  environmentInstanceName = computed(() => environment.environmentInstanceName);

  hasNotificationsUrl = signal(this.router.url.includes('/settings/notifications'));

  constructor() {
    // Load token before any API calls
    this.storage.loadToken();

    // Wire SwPush into the notification store so push subscriptions work
    this.notificationStore.setSwPush(this.swPush);

    const link: HTMLLinkElement =
      this.document.querySelector('link[rel="icon"]') || this.document.createElement('link');
    link.setAttribute('rel', 'icon');
    link.setAttribute('type', 'image/x-icon');
    link.setAttribute('href', environment.favicon);
    this.document.head.appendChild(link);

    effect(() => {
      this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
        this.hasNotificationsUrl.set(this.router.url.includes('/settings/'));
      });
    });

    this.setupRouterScroll();
    this.setupOnlineOfflineListeners();
    this.setupMsalRedirect();
    this.setupLoadingState();
    this.setupInitialData();
    this.setupLanguageEffect();
    this.setupBannerRefresh();
    effect(() => {
      this.loading.set(this.util.isLoading());
      this.loadingError.set(this.util.loadingFailed());
    });
  }

  private setupRouterScroll() {
    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((val) => {
      if (val instanceof NavigationEnd) {
        window.scrollTo(0, 0);
        const adobe: any = window.adobeDataLayer?.[0];
        if (adobe) {
          this.isCallingObj(adobe);
          adobe.web.webPageDetails['URL'] = window.location.href;
          adobe.web.webReferrer['URL'] = window.location.href;
          if (this.storage.userName()) {
            adobe._henkel.user['id'] = this.storage.userName;
          }
        }
      }
    });
  }

  private setupOnlineOfflineListeners() {
    fromEvent(window, 'online')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.connectionStatus.set('online');
        this.modalOpen.set(false);
      });

    fromEvent(window, 'offline')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.connectionStatus.set('offline');
        this.modalOpen.set(true);
      });
  }

  private setupMsalRedirect() {
    // this.msalAuth.instance
    //   .handleRedirectPromise()
    //   .then((result:any) => {
    //   })
    //   .catch((err:any) => {
    //     console.error('MSAL Redirect Error:', err);
    //   });
  }
  showAppHeader(outlet: RouterOutlet): boolean {
    return outlet?.activatedRouteData?.['appHeader'] ?? true;
  }

  private setupLoadingState() {
    this.loading.set(this.util.isLoading());
    this.loadingError.set(this.util.loadingFailed());
  }

  private setupInitialData() {
    if (this.auth.isLoggedIn()) {
      this.util.setLoadingStarted();
      forkJoin([this.assets.getRegionsEM(), this.settings.getUserSettings(), timer(1500)])
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => this.util.setLoadingFinished(),
          error: (e) => {
            if (e.status === 401) {
              this.util.setLoadingFinished();
              return;
            }
            console.error(e);
            if (environment.debugMode) alert('please check log console');
            this.util.setLoadingFinished(true);
          },
        });
      this.notifications.subscribeToNotifications();
    } else {
      this.util.setLoadingFinished();
      this.callingScript();
    }

    const adobe: any = window.adobeDataLayer?.[0];
    if (adobe) {
      adobe.userAccount['loginStatus'] = 'logged out';
      adobe.environment['type'] = environment.environmentInstanceName.toLowerCase();
    }

    document.cookie = `env=${environment.environmentInstanceName}`;
  }

  private setupLanguageEffect() {
    let isLangFetched = false;

    const setLang = (lang: string) => {
      const langMap: any = {
        en: 'en-US',
        de: 'de-DE',
        pt: 'pt-BR',
      };
      this.document.documentElement.lang = langMap[lang] || 'en-US';
    };

    const lang: any = this.i18n.languageFetched();
    if (!isLangFetched) {
      setLang(lang);
      if (!this.isOtFetched()) {
        this.callingScript();
        this.isOtFetched.set(true);
      }
    }
    isLangFetched = true;
  }

  private setupBannerRefresh() {
    this.showBannerDowntime();
    setInterval(() => this.showBannerDowntime(), this.REFRESH_INTERVAL);
  }

  private showBannerDowntime() {
    return; // remove this when need to show banner
    this.bannerMaintenance.getMaintenanceMode().then((res: any) => {
      this.showBannerMaintenance.set(res?.maintenanceMode === true);
    });
  }

  private callingScript() {
    if (document.getElementById('onetrust-consent-sdk')) {
      return;
    }
    // Initialize OneTrust window object with safe defaults to prevent TenantFeatures errors
    const win: any = window;
    if (!win.OneTrust) {
      win.OneTrust = win.OneTrust || {};
    }
    if (!win.OptanonWrapper) {
      win.OptanonWrapper = function () {
        console.log('OptanonWrapper initialized');
      };
    }

    const script = document.createElement('script');
    script.src = 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js';
    script.setAttribute('data-document-language', 'true');
    script.setAttribute('type', 'text/javascript');
    script.setAttribute('charset', 'UTF-8');
    script.setAttribute('crossorigin', 'anonymous');

    const env = environment.environmentInstanceName;
    const domainMap: any = {
      PROD: 'ae27a7fc-9246-4aee-93db-8bf4beb8e2b2',
      TEST: '2d6f8900-6dc1-4195-95e7-c26f923c1bda-test',
      default: '0c30f3b6-12ac-4d95-8d4d-98fc897c7492-test',
    };

    script.setAttribute('data-domain-script', domainMap[env] || domainMap.default);

    // Add error handler for script loading
    script.onerror = (error) => {
      console.warn('Failed to load OneTrust cookie script:', error);
      // Provide fallback to prevent app breakage
      const win: any = window;
      if (!win.OneTrust) {
        win.OneTrust = {
          TenantFeatures: {},
          IsAlertBoxClosedAndValid: () => true,
        };
      }
    };

    script.onload = () => {
      const win: any = window;
      // Ensure TenantFeatures exists even after successful load
      if (win.OneTrust && !win.OneTrust.TenantFeatures) {
        win.OneTrust.TenantFeatures = {};
      }
    };

    document.head.appendChild(script);
  }

  private isCallingObj(obj: any) {
    try {
      if (window.adobeDataLayer && Array.isArray(window.adobeDataLayer)) {
        window.adobeDataLayer.pop();
        window.adobeDataLayer.push(obj);
      }
    } catch (e) {
      console.warn('Adobe Data Layer error:', e);
    }
  }

  cancelClick() {
    this.modalOpen.set(false);
  }

  tryReconnect() {
    if (this.connectionStatus() === 'online') {
      this.modalOpen.set(false);
    }
  }

  prepareRoute(outlet: RouterOutlet) {
    return outlet?.activatedRouteData?.['animation'];
  }

  headerTransparent(outlet: RouterOutlet) {
    return outlet?.activatedRouteData?.['transparentHeader'];
  }

  onActivate(outletData: any) {
    const adobe: any = window.adobeDataLayer?.[0];
    if (adobe) {
      adobe._henkel.page['group'] = outletData.activatedRouteData.animation;
      adobe.web.webPageDetails['name'] = outletData.activatedRouteData.animation;
      adobe.web.webPageDetails['isHomePage'] =
        outletData.activatedRouteData.animation === 'Homepage';
    }

    const scrollContent = document.querySelector('#scrollContent') as HTMLElement;
    if (scrollContent) {
      scrollContent.scrollTop = 0;
    }
  }
}
